import { Suspense, lazy, type ReactNode } from "react";
import { Navigate, Route, Routes, useLocation } from "react-router-dom";

import { Spinner } from "@/components/ui";
import { useAuth } from "@/features/auth/AuthProvider";
import { LoginPage } from "@/features/auth/LoginPage";
import { SignupPage } from "@/features/auth/SignupPage";
import { DashboardPage } from "@/features/dashboard/DashboardPage";
import { ProductsPage } from "@/features/products/ProductsPage";
import { QuotePage } from "@/features/quotes/QuotePage";
import { QuotesPage } from "@/features/quotes/QuotesPage";
import { CompanyPage } from "@/features/settings/CompanyPage";
import { AccountDetailPage } from "@/features/accounts/AccountDetailPage";
import { AccountsPage } from "@/features/accounts/AccountsPage";
import { ContactDetailPage } from "@/features/contacts/ContactDetailPage";
import { ContactsPage } from "@/features/contacts/ContactsPage";
import { DealDetailPage } from "@/features/deals/DealDetailPage";
import { DealsPage } from "@/features/deals/DealsPage";
import { FieldsPage } from "@/features/settings/FieldsPage";
import { PipelinesPage } from "@/features/settings/PipelinesPage";
import { ProfilePage } from "@/features/settings/ProfilePage";
import { UsersPage } from "@/features/settings/UsersPage";
import { TasksPage } from "@/features/tasks/TasksPage";
import { AppLayout } from "./AppLayout";

// Pages most people open rarely (and the public pages customers see) load on demand.
const PublicFormPage = lazy(() => import("@/features/public/PublicFormPage").then((m) => ({ default: m.PublicFormPage })));
const PublicQuotePage = lazy(() => import("@/features/public/PublicQuotePage").then((m) => ({ default: m.PublicQuotePage })));
const StatusPage = lazy(() => import("@/features/public/StatusPage").then((m) => ({ default: m.StatusPage })));
const LegalPage = lazy(() => import("@/features/legal/LegalPage").then((m) => ({ default: m.LegalPage })));
const HelpPage = lazy(() => import("@/features/help/HelpPage").then((m) => ({ default: m.HelpPage })));
const PaidPage = lazy(() => import("@/features/public/PaidPage").then((m) => ({ default: m.PaidPage })));
const UnsubscribePage = lazy(() => import("@/features/public/UnsubscribePage").then((m) => ({ default: m.UnsubscribePage })));
const AutomationsPage = lazy(() => import("@/features/automation/AutomationsPage").then((m) => ({ default: m.AutomationsPage })));
const InboxPage = lazy(() => import("@/features/inbox/InboxPage").then((m) => ({ default: m.InboxPage })));
const LeadsPage = lazy(() => import("@/features/leads/LeadsPage").then((m) => ({ default: m.LeadsPage })));
const SequencesPage = lazy(() => import("@/features/sequences/SequencesPage").then((m) => ({ default: m.SequencesPage })));
const ChannelsPage = lazy(() => import("@/features/settings/ChannelsPage").then((m) => ({ default: m.ChannelsPage })));
const DeveloperPage = lazy(() => import("@/features/settings/DeveloperPage").then((m) => ({ default: m.DeveloperPage })));
const adminPage = <K extends keyof typeof import("@/features/admin/AdminPages")>(name: K) =>
  lazy(() => import("@/features/admin/AdminPages").then((m) => ({ default: m[name] as React.ComponentType })));
const AdminLoginPage = adminPage("AdminLoginPage");
const AdminLayout = adminPage("AdminLayout");
const AdminTenantsPage = adminPage("AdminTenantsPage");
const AdminTenantPage = adminPage("AdminTenantPage");
const AdminAuditPage = adminPage("AdminAuditPage");
const AdminAccountPage = adminPage("AdminAccountPage");
const OrdersPage = lazy(() => import("@/features/orders/OrdersPage").then((m) => ({ default: m.OrdersPage })));
const OrderPage = lazy(() => import("@/features/orders/OrderPage").then((m) => ({ default: m.OrderPage })));
const VisitsPage = lazy(() => import("@/features/visits/VisitsPage").then((m) => ({ default: m.VisitsPage })));
const PricingPage = lazy(() => import("@/features/settings/PricingPage").then((m) => ({ default: m.PricingPage })));
const TerritoriesPage = lazy(() => import("@/features/settings/TerritoriesPage").then((m) => ({ default: m.TerritoriesPage })));
const AccountingPage = lazy(() => import("@/features/settings/AccountingPage").then((m) => ({ default: m.AccountingPage })));
const ApprovalsPage = lazy(() => import("@/features/approvals/ApprovalsPage").then((m) => ({ default: m.ApprovalsPage })));
const SamplesPage = lazy(() => import("@/features/samples/SamplesPage").then((m) => ({ default: m.SamplesPage })));
const TradeShowsPage = lazy(() => import("@/features/shows/TradeShowsPage").then((m) => ({ default: m.TradeShowsPage })));
const CapturePage = lazy(() => import("@/features/shows/CapturePage").then((m) => ({ default: m.CapturePage })));
const ReportsPage = lazy(() => import("@/features/reports/ReportsPage").then((m) => ({ default: m.ReportsPage })));
const TicketsPage = lazy(() => import("@/features/service/TicketsPage").then((m) => ({ default: m.TicketsPage })));
const TicketPage = lazy(() => import("@/features/service/TicketPage").then((m) => ({ default: m.TicketPage })));
const AssetsPage = lazy(() => import("@/features/service/AssetsPage").then((m) => ({ default: m.AssetsPage })));
const ContractsPage = lazy(() => import("@/features/service/AmcPages").then((m) => ({ default: m.ContractsPage })));
const ContractPage = lazy(() => import("@/features/service/AmcPages").then((m) => ({ default: m.ContractPage })));
const ReportBuilderPage = lazy(() => import("@/features/reports/ReportBuilderPage").then((m) => ({ default: m.ReportBuilderPage })));
const ForecastPage = lazy(() => import("@/features/reports/ForecastPage").then((m) => ({ default: m.ForecastPage })));
const PlanPage = lazy(() => import("@/features/settings/PlanPage").then((m) => ({ default: m.PlanPage })));
const SsoPage = lazy(() => import("@/features/settings/SsoPage").then((m) => ({ default: m.SsoPage })));
const IntegrationsPage = lazy(() => import("@/features/settings/IntegrationsPage").then((m) => ({ default: m.IntegrationsPage })));
const AdminPlansPage = adminPage("AdminPlansPage");
const AdminPartnersPage = adminPage("AdminPartnersPage");
const AdminSupportPage = adminPage("AdminSupportPage");
const AdminStatusPage = adminPage("AdminStatusPage");
const ImportPage = lazy(() => import("@/features/data/ImportPage").then((m) => ({ default: m.ImportPage })));

function RequireAuth({ children }: { children: ReactNode }) {
  const { status } = useAuth();
  const location = useLocation();
  if (status === "loading") return <div className="p-10"><Spinner /></div>;
  if (status === "guest") return <Navigate to="/login" replace state={{ from: location.pathname }} />;
  return <>{children}</>;
}

function GuestOnly({ children }: { children: ReactNode }) {
  const { status } = useAuth();
  if (status === "loading") return <div className="p-10"><Spinner /></div>;
  if (status === "authed") return <Navigate to="/" replace />;
  return <>{children}</>;
}

export function AppRoutes() {
  return (
    <Suspense fallback={<div className="p-10"><Spinner /></div>}>
    <Routes>
      <Route path="/login" element={<GuestOnly><LoginPage /></GuestOnly>} />
      <Route path="/signup" element={<GuestOnly><SignupPage /></GuestOnly>} />
      <Route path="/q/:token" element={<PublicQuotePage />} />
      <Route path="/f/:token" element={<PublicFormPage />} />
      <Route path="/u/:token" element={<UnsubscribePage />} />
      <Route path="/paid" element={<PaidPage />} />
      <Route path="/status" element={<StatusPage />} />
      <Route path="/legal/:doc" element={<LegalPage />} />
      <Route path="/admin/login" element={<AdminLoginPage />} />
      <Route path="/admin" element={<AdminLayout />}>
        <Route index element={<AdminTenantsPage />} />
        <Route path="tenants/:id" element={<AdminTenantPage />} />
        <Route path="audit" element={<AdminAuditPage />} />
        <Route path="plans" element={<AdminPlansPage />} />
        <Route path="partners" element={<AdminPartnersPage />} />
        <Route path="support" element={<AdminSupportPage />} />
        <Route path="support/:id" element={<AdminSupportPage />} />
        <Route path="status" element={<AdminStatusPage />} />
        <Route path="account" element={<AdminAccountPage />} />
      </Route>
      <Route element={<RequireAuth><AppLayout /></RequireAuth>}>
        <Route path="/contacts" element={<ContactsPage />} />
        <Route path="/contacts/:id" element={<ContactDetailPage />} />
        <Route path="/" element={<DashboardPage />} />
        <Route path="/products" element={<ProductsPage />} />
        <Route path="/quotes" element={<QuotesPage />} />
        <Route path="/quotes/new" element={<QuotePage />} />
        <Route path="/quotes/:id" element={<QuotePage />} />
        <Route path="/settings/company" element={<CompanyPage />} />
        <Route path="/orders" element={<OrdersPage />} />
        <Route path="/orders/:id" element={<OrderPage />} />
        <Route path="/visits" element={<VisitsPage />} />
        <Route path="/approvals" element={<ApprovalsPage />} />
        <Route path="/samples" element={<SamplesPage />} />
        <Route path="/trade-shows" element={<TradeShowsPage />} />
        <Route path="/trade-shows/:id" element={<TradeShowsPage />} />
        <Route path="/capture/:id" element={<CapturePage />} />
        <Route path="/reports" element={<ReportsPage />} />
        <Route path="/reports/builder" element={<ReportBuilderPage />} />
        <Route path="/forecast" element={<ForecastPage />} />
        <Route path="/service" element={<TicketsPage />} />
        <Route path="/service/:id" element={<TicketPage />} />
        <Route path="/installed-base" element={<AssetsPage />} />
        <Route path="/amc" element={<ContractsPage />} />
        <Route path="/amc/:id" element={<ContractPage />} />
        <Route path="/settings/pricing" element={<PricingPage />} />
        <Route path="/settings/territories" element={<TerritoriesPage />} />
        <Route path="/settings/accounting" element={<AccountingPage />} />
        <Route path="/settings/plan" element={<PlanPage />} />
        <Route path="/help" element={<HelpPage />} />
        <Route path="/settings/sso" element={<SsoPage />} />
        <Route path="/settings/integrations" element={<IntegrationsPage />} />
        <Route path="/settings/profile" element={<ProfilePage />} />
        <Route path="/settings/channels" element={<ChannelsPage />} />
        <Route path="/inbox" element={<InboxPage />} />
        <Route path="/leads" element={<LeadsPage />} />
        <Route path="/automations" element={<AutomationsPage />} />
        <Route path="/sequences" element={<SequencesPage />} />
        <Route path="/settings/developer" element={<DeveloperPage />} />
        <Route path="/deals" element={<DealsPage />} />
        <Route path="/deals/:id" element={<DealDetailPage />} />
        <Route path="/accounts" element={<AccountsPage />} />
        <Route path="/accounts/:id" element={<AccountDetailPage />} />
        <Route path="/tasks" element={<TasksPage />} />
        <Route path="/data" element={<ImportPage />} />
        <Route path="/settings/pipelines" element={<PipelinesPage />} />
        <Route path="/settings/fields" element={<FieldsPage />} />
        <Route path="/settings/users" element={<UsersPage />} />
      </Route>
      <Route path="*" element={<Navigate to="/" replace />} />
    </Routes>
    </Suspense>
  );
}
